/**
 * Course player page.
 */
import React, { useEffect, useState } from "react";
import { Icon } from '@iconify/react';
import MuiDrawer from '@mui/material/Drawer';
import { Box, Grid, Divider } from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import BoardSidebar from "./board.sidebar";
import BoardContent from "./board.content";
import BoardProgressBar from "./board.progressbar";
import BoardBreadCrumb from "./board.breadcrumb";
import { useAsterController } from "context";
import { actionAppCourseDetailGet } from "context/action";
import BoardIdle from "./board.idle";
import { hasVerticalAt, shouldFetchCourseDetail } from "utils/course";

/**
 * Loads the current unit and composes sidebar, content, pagination, and idle tracking.
 */
const Board = () => {

  const { t } = useTranslation('common');

  const { courseId, sectionIndex, sequenceIndex, verticalIndex } = useParams();
  const navigate = useNavigate();

  const [controller, dispatch] = useAsterController();
  const { mobile, structure, loadedDashboard, loadedCourseDetail, courses, sessionHydrated, firstLoadPending } = controller;

  const [wrap, setWrap] = useState(true);

  useEffect(() => {
    if (!shouldFetchCourseDetail(sessionHydrated, courseId, firstLoadPending)) return;
    actionAppCourseDetailGet(dispatch, courseId);
  }, [sessionHydrated, firstLoadPending, courseId, dispatch]);

  useEffect(() => {
    if (!sessionHydrated || !loadedDashboard) return;
    const currCourse = (courses.data || []).find(v => v.id === courseId) || {};
    if (!currCourse.enrollment_status) navigate('/dashboard');
  }, [sessionHydrated, courseId, courses.data, loadedDashboard, navigate]);

  useEffect(() => {
    if (!loadedCourseDetail || !structure || structure.course_id !== courseId) return;
    if (!Array.isArray(structure.sections)) return;
    if (hasVerticalAt(structure, sectionIndex, sequenceIndex, verticalIndex)) return;
    navigate(`/course/${courseId}`, { replace: true });
  }, [loadedCourseDetail, structure, courseId, sectionIndex, sequenceIndex, verticalIndex, navigate]);

  return <>
    <BoardIdle />

    {
      mobile && <MuiDrawer anchor="left" open={!wrap} onClose={() => setWrap(!wrap)} >
        <Box className="w-80">
          <BoardSidebar setMobileSidebar={setWrap} />
        </Box>
      </MuiDrawer>
    }

    <Box className="m-0 md:m-6 v-bg-body rounded-none md:rounded-md flex-1">
      <Box className="min-h-full mx-auto">
        <Box className="md:px-8 px-4 pt-6">
          <Grid container spacing={2}>
            <Grid item xs={12} className="flex justify-between">
              <Link to={`/course/${courseId}`} className="no-underline">
                <VButton color="secondary" className="text-lg">
                  <Icon icon="bi:chevron-left" className="text-sm flex-shrink-0" />&nbsp;{t("Back")}
                </VButton>
              </Link>
              <VButton onClick={() => setWrap(false)} iconOnly className="block md:hidden">
                <Icon icon="heroicons-solid:menu-alt-1" />
              </VButton>
            </Grid>
            <Grid item xs={12} md={8}>
              <VText className="text-4xl font-bold mt-4" div>{structure.course_title}</VText>
              <BoardBreadCrumb />
            </Grid>
            <Grid item xs={12} md={4} className="flex items-center justify-center md:justify-end">
              <BoardProgressBar />
            </Grid>
          </Grid>
        </Box>

        <Divider className="hidden md:flex mt-5 border-gray-200 dark:border-gray-500" />

        {/******************************* Content ********************************/}
        <Box className="flex justify-between">
          <Box className="hidden md:block z-0">
            <BoardSidebar setMobileSidebar={setWrap} />
          </Box>
          <BoardContent />
        </Box>

        <Divider className="hidden md:flex my-b border-gray-200 dark:border-gray-500" />

        
      </Box>
    </Box>
  </>;
};

export default Board;